/**
 * SignatureCapture - Componente para captura de assinatura digital
 *
 * Usa canvas HTML5 para desenho da assinatura
 * Suporta touch e mouse
 * Exporta como base64 PNG
 */

import {
  useRef, useState, useEffect, useCallback,
} from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Clear as ClearIcon,
  Undo as UndoIcon,
  Check as ConfirmIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';

const SignatureCapture = ({
  onSignatureChange,
  onConfirm,
  width = 400,
  height = 200,
  strokeColor = '#000000',
  strokeWidth = 2,
  backgroundColor = '#ffffff',
  placeholder = 'Assine aqui',
  disabled = false,
  initialSignature = null,
}) => {
  const tokens = usePremiumTokens();
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [history, setHistory] = useState([]);
  const [, setLastPoint] = useState(null);

  // Inicializar canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');

    // Configurar canvas para alta resolucao (retina)
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.scale(dpr, dpr);

    // Fundo
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, width, height);

    // Linha guia
    ctx.strokeStyle = '#e0e0e0';
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 5]);
    ctx.beginPath();
    ctx.moveTo(20, height - 40);
    ctx.lineTo(width - 20, height - 40);
    ctx.stroke();
    ctx.setLineDash([]);

    // Carregar assinatura inicial se existir
    if (initialSignature) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, width, height);
        setHasSignature(true);
      };
      img.src = initialSignature;
    }

    // Salvar estado inicial
    saveState();
  }, [width, height, backgroundColor, initialSignature]);

  // Salvar estado para undo
  const saveState = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dataUrl = canvas.toDataURL();
    setHistory((prev) => [...prev.slice(-10), dataUrl]); // Manter ultimos 10 estados
  }, []);

  // Obter posicao do evento (mouse ou touch)
  const getEventPosition = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();

    if (e.touches && e.touches.length > 0) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    }

    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  }, []);

  // Iniciar desenho
  const startDrawing = useCallback((e) => {
    if (disabled) return;

    e.preventDefault();
    const pos = getEventPosition(e);

    setIsDrawing(true);
    setLastPoint(pos);

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = strokeWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  }, [disabled, getEventPosition, strokeColor, strokeWidth]);

  // Desenhar
  const draw = useCallback((e) => {
    if (!isDrawing || disabled) return;

    e.preventDefault();
    const pos = getEventPosition(e);

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();

    setLastPoint(pos);
    setHasSignature(true);
  }, [isDrawing, disabled, getEventPosition]);

  // Finalizar desenho
  const stopDrawing = useCallback((e) => {
    if (!isDrawing) return;

    e?.preventDefault();
    setIsDrawing(false);
    setLastPoint(null);

    // Salvar estado e notificar mudanca
    saveState();

    if (onSignatureChange && hasSignature) {
      const canvas = canvasRef.current;
      onSignatureChange(canvas.toDataURL('image/png'));
    }
  }, [isDrawing, hasSignature, saveState, onSignatureChange]);

  // Limpar canvas
  const clearSignature = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');

    // Limpar e redesenhar fundo
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, width, height);

    // Redesenhar linha guia
    ctx.strokeStyle = '#e0e0e0';
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 5]);
    ctx.beginPath();
    ctx.moveTo(20, height - 40);
    ctx.lineTo(width - 20, height - 40);
    ctx.stroke();
    ctx.setLineDash([]);

    setHasSignature(false);
    setHistory([]);

    if (onSignatureChange) {
      onSignatureChange(null);
    }
  }, [backgroundColor, width, height, onSignatureChange]);

  // Desfazer ultima acao
  const undo = useCallback(() => {
    if (history.length <= 1) {
      clearSignature();
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const newHistory = [...history];
    newHistory.pop(); // Remove estado atual

    const previousState = newHistory[newHistory.length - 1];

    if (previousState) {
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        setHistory(newHistory);
      };
      img.src = previousState;
    }
  }, [history, width, height, clearSignature]);

  // Confirmar assinatura
  const handleConfirm = useCallback(() => {
    if (!hasSignature) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const signatureData = canvas.toDataURL('image/png');

    if (onConfirm) {
      onConfirm(signatureData);
    }
  }, [hasSignature, onConfirm]);

  // Obter assinatura atual - disponível para uso futuro via ref
  // const getSignature = useCallback(() => {
  //   const canvas = canvasRef.current;
  //   if (!canvas || !hasSignature) return null;
  //   return canvas.toDataURL('image/png');
  // }, [hasSignature]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      {/* Canvas */}
      <Box
        sx={{
          position: 'relative',
          width,
          height,
          borderRadius: tokens?.radius?.md || '8px',
          border: `2px solid ${hasSignature ? tokens?.accent?.primary : tokens?.border?.default}`,
          overflow: 'hidden',
          backgroundColor,
          cursor: disabled ? 'not-allowed' : 'crosshair',
          opacity: disabled ? 0.6 : 1,
          touchAction: 'none', // Prevenir scroll durante touch
        }}
      >
        <canvas
          ref={canvasRef}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          style={{
            display: 'block',
          }}
        />

        {/* Placeholder */}
        {!hasSignature && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <EditIcon sx={{ color: tokens?.text?.tertiary, fontSize: 32, opacity: 0.5 }} />
            <Typography
              sx={{
                color: tokens?.text?.tertiary,
                fontSize: '14px',
                opacity: 0.7,
              }}
            >
              {placeholder}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Controles */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Desfazer">
            <span>
              <IconButton
                size="small"
                onClick={undo}
                disabled={disabled || history.length <= 1}
                sx={{ color: tokens?.text?.secondary }}
              >
                <UndoIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Limpar">
            <span>
              <IconButton
                size="small"
                onClick={clearSignature}
                disabled={disabled || !hasSignature}
                sx={{ color: tokens?.text?.secondary }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {onConfirm && (
          <Button
            size="small"
            variant="contained"
            startIcon={<ConfirmIcon />}
            onClick={handleConfirm}
            disabled={disabled || !hasSignature}
            sx={{
              backgroundColor: tokens?.semantic?.success,
              '&:hover': { backgroundColor: '#16a34a' },
            }}
          >
            Confirmar
          </Button>
        )}
      </Box>

      {/* Indicador de status */}
      {hasSignature && (
        <Typography
          sx={{
            color: tokens?.semantic?.success,
            fontSize: '12px',
            textAlign: 'center',
          }}
        >
          Assinatura capturada
        </Typography>
      )}
    </Box>
  );
};

export default SignatureCapture;
